/**
 * TagChips Component
 * Displays tags as small colored chips with optional icons
 */

import React from 'react';
import clsx from 'clsx';

export interface Tag {
  label: string;
  icon?: string;
  color?: string;
  category?: 'tipo' | 'accion' | 'caracteristica' | 'ubicacion';
}

interface TagChipsProps {
  tags: Tag[];
  maxVisible?: number;
}

// Tag categories with default colors
const categoryColors = {
  tipo: '#dbeafe',       // blue - Oficial, Blog, Tienda, Noticias, Foro
  accion: '#dcfce7',     // green - Reservar, Comprar, Descargar, Contactar
  caracteristica: '#fef3c7', // yellow - Gratis, Premium, Verificado, Popular
  ubicacion: '#fce7f3',  // pink - Local, Nacional, Internacional
};

const defaultColors = [
  '#dbeafe', // blue
  '#dcfce7', // green
  '#fef3c7', // yellow
  '#fce7f3', // pink
  '#e0e7ff', // indigo
  '#f3e8ff', // purple
];

export const TagChips: React.FC<TagChipsProps> = ({ tags, maxVisible = 5 }) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  const visibleTags = tags.slice(0, maxVisible);
  const remainingCount = tags.length - maxVisible;

  return (
    <div className="ai360-tag-chips">
      {visibleTags.map((tag, index) => {
        // Use category color if available, otherwise use tag color or default
        const backgroundColor = tag.category 
          ? categoryColors[tag.category]
          : (tag.color || defaultColors[index % defaultColors.length]);
        
        return (
          <span
            key={`${tag.label}-${index}`}
            className="ai360-tag-chip"
            style={{ backgroundColor }}
            title={tag.category ? `Categoría: ${tag.category}` : undefined}
            aria-label={tag.category ? `Tag de categoría ${tag.category}: ${tag.label}` : tag.label}
          >
            {tag.icon && (
              <span className="ai360-tag-chip-icon" role="img" aria-label="tag icon">
                {tag.icon}
              </span>
            )}
            <span className="ai360-tag-chip-label">{tag.label}</span>
          </span>
        );
      })}
      
      {remainingCount > 0 && (
        <span className="ai360-tag-chip ai360-tag-chip--more">
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

// Helper function to create tags with categories
export const createTag = (
  label: string,
  category?: Tag['category'],
  icon?: string
): Tag => ({
  label,
  category,
  icon,
});

// Predefined tag templates by category
export const TAG_TEMPLATES = {
  tipo: {
    oficial: createTag('Oficial', 'tipo', '🏛️'),
    blog: createTag('Blog', 'tipo', '📝'),
    tienda: createTag('Tienda', 'tipo', '🛒'),
    noticias: createTag('Noticias', 'tipo', '📰'),
    foro: createTag('Foro', 'tipo', '💬'),
  },
  accion: {
    reservar: createTag('Reservar', 'accion', '📅'),
    comprar: createTag('Comprar', 'accion', '🛍️'),
    descargar: createTag('Descargar', 'accion', '⬇️'),
    contactar: createTag('Contactar', 'accion', '📧'),
  },
  caracteristica: {
    gratis: createTag('Gratis', 'caracteristica', '🆓'),
    premium: createTag('Premium', 'caracteristica', '⭐'),
    verificado: createTag('Verificado', 'caracteristica', '✓'),
    popular: createTag('Popular', 'caracteristica', '🔥'),
  },
  ubicacion: {
    local: createTag('Local', 'ubicacion', '📍'),
    nacional: createTag('Nacional', 'ubicacion', '🏳️'),
    internacional: createTag('Internacional', 'ubicacion', '🌍'),
  },
};
